// Slice 33 — SQLite rename emitter.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 33.
//
// SQLite has the narrowest surface of the five engines:
//   - No stored procedures or scalar functions (no module-kind body rewrites).
//   - No `ALTER VIEW` of any shape — view body rewrites and view rename go
//     through DROP + CREATE.
//   - Triggers rewrite via DROP + CREATE (no `ALTER TRIGGER` form).
//   - DEFAULT / CHECK constraint changes require a full table rebuild
//     (CREATE new table, copy rows, DROP old, RENAME new) — explicitly out of
//     v1 scope. Constraint kinds bubble up an Error so the planner surfaces
//     the limitation in the preview dialog.
//
// SQLite's default schema is `main`. Identifiers are quoted with double quotes
// (SQLite also accepts `[…]` and backticks but double quotes are the SQL-
// standard form and what we emit consistently across all engines that allow
// them).
//
// Schema-rename: SQLite supports schema-qualified `ALTER TABLE "main"."old"
// RENAME TO "new"` — the new name is unqualified (matches Oracle; any
// schema-qualified target is rejected by the parser).
//
// Atomicity note: The PRD calls for view body rewrites to be "wrapped in
// `BEGIN; … COMMIT;`" but the rename executor (`renameExecutor.ts`) already
// includes `sqlite` in `TRANSACTIONAL_ENGINES` and wraps the entire batch in
// `BEGIN`/`COMMIT`. Emitting an inner `BEGIN` inside that outer transaction
// errors with "cannot start a transaction within a transaction" in
// better-sqlite3 (and SQLite generally — nested transactions need SAVEPOINTs,
// not BEGIN/COMMIT). We therefore emit `DROP + CREATE` as a multi-statement
// SQL string without an inner BEGIN/COMMIT — the executor's outer transaction
// is what makes the pair atomic. Production correctness > literal PRD text.

import type { ObjectKind, QualifiedName } from '../../types';

import type {
  DialectRenameEmitter,
  EmitObjectBodyAlterInput,
} from './types';

const DEFAULT_SCHEMA = 'main';

function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function schemaOf(qn: QualifiedName): string {
  return qn.schema && qn.schema.length > 0 ? qn.schema : DEFAULT_SCHEMA;
}

function quotedQualified(qn: QualifiedName): string {
  return `${quoteIdent(schemaOf(qn))}.${quoteIdent(qn.name)}`;
}

/** Substitute the leading `CREATE` (or `CREATE OR REPLACE`, `CREATE TEMP`,
 *  etc.) in a CREATE-form body with plain `CREATE`. SQLite has no
 *  `OR REPLACE` form, so any incoming OR-REPLACE shape is normalised. */
function ensurePlainCreate(body: string): string {
  return body.replace(/^\s*create\s+(or\s+replace\s+)?/i, 'CREATE ');
}

function emitViewBodyAlter(qn: QualifiedName, newBody: string): string {
  // SQLite has no `ALTER VIEW` form; the only safe path is DROP + CREATE.
  // The introspection feed (`sqlite_schema.sql`) returns the FULL
  // `CREATE VIEW …` statement, so the emitter expects `newBody` to already
  // include the leading CREATE. Normalise any OR-REPLACE shape to plain
  // `CREATE` (SQLite rejects `CREATE OR REPLACE`). Atomicity is provided by
  // the executor's outer transaction — see file header.
  const fq = quotedQualified(qn);
  const create = ensurePlainCreate(newBody.trimEnd().replace(/;\s*$/, '').trim());
  return `DROP VIEW IF EXISTS ${fq};\n${create};`;
}

function emitTriggerBodyAlter(qn: QualifiedName, newBody: string): string {
  // SQLite has no `ALTER TRIGGER` form — DROP + CREATE.
  // `sqlite_schema.sql` returns the full `CREATE TRIGGER …` statement.
  const fq = quotedQualified(qn);
  const create = ensurePlainCreate(newBody.trimEnd().replace(/;\s*$/, '').trim());
  return `DROP TRIGGER IF EXISTS ${fq};\n${create};`;
}

const CONSTRAINT_REWRITE_NOT_SUPPORTED =
  'SQLite constraint rewrite requires table rebuild — not supported in v1';

class NotSupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotSupportedError';
  }
}

export function emitObjectBodyAlter(input: EmitObjectBodyAlterInput): string {
  const { objectKind, objectName, newBody } = input;
  if (objectKind === 'check-constraint' || objectKind === 'default-constraint') {
    throw new NotSupportedError(CONSTRAINT_REWRITE_NOT_SUPPORTED);
  }
  if (objectKind === 'view' || objectKind === 'materialized-view') {
    return emitViewBodyAlter(objectName, newBody);
  }
  if (objectKind === 'trigger') {
    return emitTriggerBodyAlter(objectName, newBody);
  }
  // Module kinds (inline-tvf / scalar-function / procedure) don't exist in
  // SQLite. The planner shouldn't reach this branch on a SQLite connection;
  // surface as NotSupported if it ever does.
  throw new NotSupportedError(
    `SQLite has no ${objectKind} object kind — body rewrite is not supported`,
  );
}

export type SchemaRenameInput = {
  readonly kind: 'table' | 'view';
  readonly from: QualifiedName;
  readonly to: QualifiedName;
};

export function emitTableOrViewSchemaAlter(input: SchemaRenameInput): string {
  if (input.kind === 'table') {
    // SQLite supports `ALTER TABLE [sch.]old RENAME TO new`. The TO target
    // is unqualified (the new name lives in the same schema as the source —
    // SQLite rejects a schema-qualified target).
    return `ALTER TABLE ${quotedQualified(input.from)} RENAME TO ${quoteIdent(input.to.name)}`;
  }
  // Views (NAV-2): modern SQLite (3.25+) renames a view non-destructively
  // with the SAME `ALTER TABLE <old> RENAME TO <new>` statement used for
  // tables — SQLite keeps tables and views in one object namespace and the
  // ALTER TABLE … RENAME machinery operates on either, preserving the view's
  // real definition. This replaces the old data-destroying `CREATE VIEW new
  // AS SELECT * FROM old; DROP VIEW old;` placeholder, which both broke the
  // new view and irrecoverably lost the original SELECT body. The TO target
  // is unqualified (the new name lives in the source's schema; a
  // schema-qualified target is rejected by the parser).
  return `ALTER TABLE ${quotedQualified(input.from)} RENAME TO ${quoteIdent(input.to.name)}`;
}

export type EmitUndoAlterInput =
  | {
      readonly kind: 'body';
      readonly objectKind: ObjectKind;
      readonly objectName: QualifiedName;
      readonly originalBody: string;
    }
  | {
      readonly kind: 'schema';
      readonly schemaRename: SchemaRenameInput;
    };

export function emitUndoAlter(input: EmitUndoAlterInput): string {
  if (input.kind === 'body') {
    return emitObjectBodyAlter({
      objectKind: input.objectKind,
      objectName: input.objectName,
      newBody: input.originalBody,
    });
  }
  return emitTableOrViewSchemaAlter({
    kind: input.schemaRename.kind,
    from: input.schemaRename.to,
    to: input.schemaRename.from,
  });
}

export const sqliteRenameEmitter: DialectRenameEmitter = {
  emitObjectBodyAlter,
  emitTableOrViewSchemaAlter,
};

export { NotSupportedError, CONSTRAINT_REWRITE_NOT_SUPPORTED };
